/**
 * One Rep Max (1RM) Estimation Service
 *
 * Calculates estimated 1RM from workout data using multiple formulas:
 * - Epley Formula: Most commonly used
 * - Brzycki Formula: More conservative
 * - Lander Formula: Good for higher reps
 *
 * Uses average of all three for better accuracy.
 */

import { supabase } from '../supabase';

/**
 * Calculate 1RM using Epley formula
 * Formula: 1RM = weight × (1 + reps/30)
 * Best for: 1-10 reps
 *
 * @param {number} weight - Weight lifted (kg)
 * @param {number} reps - Reps performed
 * @returns {number} - Estimated 1RM
 */
export const calculateEpley = (weight, reps) => {
  if (reps === 1) return weight;
  return weight * (1 + reps / 30);
};

/**
 * Calculate 1RM using Brzycki formula
 * Formula: 1RM = weight × (36 / (37 - reps))
 * Best for: 1-10 reps
 *
 * @param {number} weight - Weight lifted (kg)
 * @param {number} reps - Reps performed
 * @returns {number} - Estimated 1RM
 */
export const calculateBrzycki = (weight, reps) => {
  if (reps === 1) return weight;
  if (reps >= 37) return null; // Formula breaks down at high reps
  return weight * (36 / (37 - reps));
};

/**
 * Calculate 1RM using Lander formula
 * Formula: 1RM = (100 × weight) / (101.3 - 2.67123 × reps)
 * Best for: Higher rep ranges
 *
 * @param {number} weight - Weight lifted (kg)
 * @param {number} reps - Reps performed
 * @returns {number} - Estimated 1RM
 */
export const calculateLander = (weight, reps) => {
  if (reps === 1) return weight;
  return (100 * weight) / (101.3 - 2.67123 * reps);
};

/**
 * Calculate average 1RM from multiple formulas
 * Uses all applicable formulas and averages them
 *
 * @param {number} weight - Weight lifted (kg)
 * @param {number} reps - Reps performed
 * @returns {number} - Estimated 1RM (averaged)
 */
export const calculateOneRM = (weight, reps) => {
  if (!weight || !reps) return null;
  if (reps === 1) return weight;

  // Don't estimate 1RM for high rep sets (>15 reps)
  // The formulas become unreliable
  if (reps > 15) return null;

  const estimates = [];

  // Epley (good for all ranges)
  estimates.push(calculateEpley(weight, reps));

  // Brzycki (good for low-medium reps)
  if (reps < 37) {
    const brzycki = calculateBrzycki(weight, reps);
    if (brzycki) estimates.push(brzycki);
  }

  // Lander (good for higher reps)
  const lander = calculateLander(weight, reps);
  if (lander) estimates.push(lander);

  // Average all estimates
  const average = estimates.reduce((sum, val) => sum + val, 0) / estimates.length;
  return Math.round(average * 10) / 10; // Round to 1 decimal
};


/**
 * Get the best estimated 1RM for an exercise
 * Looks at recent sets (last 90 days) and picks the highest 1RM estimate
 *
 * @param {string} userId - User's auth ID
 * @param {string} exerciseName - Exercise name (from snapshot)
 * @param {boolean} isBodyweight - Skip: logged weight includes user bodyweight, not just load
 * @param {boolean} isAssisted - Skip: logged weight is assistance amount, not load
 * @returns {Object|null} - { estimated1RM, weight, reps, date } or null
 */
export const getBestEstimated1RM = async (userId, exerciseName, isBodyweight = false, isAssisted = false) => {
  // 1RM formulas only make sense for regular weighted exercises
  if (isBodyweight || isAssisted) return null;

  try {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    const { data: logs, error } = await supabase
      .from('workout_logs')
      .select('weight, reps, created_at')
      .eq('user_id', userId)
      .eq('completed', true) // finished sets only (task 017, M1)
      .eq('exercise_name', exerciseName)
      .gte('created_at', ninetyDaysAgo.toISOString())
      .gte('reps', 1)
      .lte('reps', 15)
      .not('weight', 'is', null)
      .order('created_at', { ascending: false });

    if (error) throw error;
    if (!logs || logs.length === 0) return null;

    let best = null;

    logs.forEach(log => {
      const estimated1RM = calculateOneRM(log.weight, log.reps);
      if (estimated1RM && (!best || estimated1RM > best.estimated1RM)) {
        best = {
          estimated1RM,
          weight: log.weight,
          reps: log.reps,
          date: log.created_at,
        };
      }
    });

    return best;
  } catch (error) {
    console.error('Error getting best 1RM:', error);
    return null;
  }
};

/**
 * Get 1RM progression over time for an exercise
 * Shows how estimated 1RM has improved
 *
 * @param {string} userId - User's auth ID
 * @param {string} exerciseName - Exercise name
 * @param {number} months - Number of months to look back (default 6)
 * @param {boolean} isBodyweight - Skip if true (1RM not meaningful)
 * @param {boolean} isAssisted - Skip if true (1RM not meaningful)
 * @returns {Array} - Array of {date, estimated1RM, weight, reps}
 */
export const get1RMProgression = async (userId, exerciseName, months = 6, isBodyweight = false, isAssisted = false) => {
  if (isBodyweight || isAssisted) return [];
  try {
    const monthsAgo = new Date();
    monthsAgo.setMonth(monthsAgo.getMonth() - months);

    const { data: logs, error } = await supabase
      .from('workout_logs')
      .select('weight, reps, created_at')
      .eq('user_id', userId)
      .eq('completed', true) // finished sets only (task 017, M1)
      .eq('exercise_name', exerciseName)
      .gte('created_at', monthsAgo.toISOString())
      .gte('reps', 1)
      .lte('reps', 15)
      .not('weight', 'is', null)
      .order('created_at', { ascending: true });

    if (error) throw error;
    if (!logs || logs.length === 0) return [];

    // Group by calendar day — keep only the best 1RM per day
    const dailyBest = new Map();
    logs.forEach(log => {
      const day = log.created_at.slice(0, 10);
      const estimated1RM = calculateOneRM(log.weight, log.reps);
      if (!estimated1RM) return;
      const existing = dailyBest.get(day);
      if (!existing || estimated1RM > existing.estimated1RM) {
        dailyBest.set(day, { date: day, estimated1RM, weight: log.weight, reps: log.reps });
      }
    });

    // Build progression — all daily bests sorted chronologically
    const progression = Array.from(dailyBest.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, best]) => best);

    return progression;
  } catch (error) {
    console.error('Error getting 1RM progression:', error);
    return [];
  }
};

/**
 * Get estimated 1RMs for all exercises a user has logged
 * Useful for showing a list of all lifts with their estimated maxes
 * Excludes bodyweight and assisted exercises (1RM formulas don't apply)
 *
 * @param {string} userId - User's auth ID
 * @returns {Array} - Array of {exerciseName, movementCategory, estimated1RM, weight, reps, date}
 */
export const getAllEstimated1RMs = async (userId) => {
  try {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    // Include exercise_id so we can look up is_bodyweight / is_assisted
    // Accept exercise_type = 'resistance' OR null (older logs before the column existed)
    // Weight + reps filters already exclude cardio/stretch logs
    const { data: logs, error } = await supabase
      .from('workout_logs')
      .select('exercise_id, exercise_name, movement_category, weight, reps, created_at')
      .eq('user_id', userId)
      .eq('completed', true) // finished sets only (task 017, M1)
      .or('exercise_type.eq.resistance,exercise_type.is.null')
      .gte('created_at', ninetyDaysAgo.toISOString())
      .gte('reps', 1)
      .lte('reps', 15)
      .not('weight', 'is', null)
      .not('exercise_name', 'is', null)
      .order('created_at', { ascending: false });

    if (error) throw error;
    if (!logs || logs.length === 0) return [];

    // Fetch exercise flags for all distinct exercise IDs
    const exerciseIds = [...new Set(logs.map(l => l.exercise_id).filter(Boolean))];
    let exerciseFlagMap = new Map();
    if (exerciseIds.length > 0) {
      const { data: exercises } = await supabase
        .from('exercises')
        .select('id, is_bodyweight, is_assisted')
        .in('id', exerciseIds);
      exerciseFlagMap = new Map((exercises || []).map(e => [e.id, e]));
    }

    // Group by exercise, skip bodyweight/assisted, find best 1RM for each —
    // also tracks the actual lightest/heaviest weight logged (not 1RM-derived)
    // and the most recent session date, for callers that want a fuller
    // per-exercise picture than just the single best set.
    const exerciseMap = new Map();

    logs.forEach(log => {
      const flags = exerciseFlagMap.get(log.exercise_id);

      // Skip: 1RM formulas don't apply to bodyweight or assisted exercises
      if (flags?.is_bodyweight || flags?.is_assisted) return;

      const weight = parseFloat(log.weight);
      const estimated1RM = calculateOneRM(log.weight, log.reps);

      let entry = exerciseMap.get(log.exercise_name);
      if (!entry) {
        entry = {
          exerciseName: log.exercise_name,
          movementCategory: log.movement_category,
          estimated1RM: 0,
          weight: null,
          reps: null,
          date: null,
          minWeight: null,
          maxWeight: null,
          // logs are fetched newest-first (order('created_at', {ascending:false})
          // above), so the first log seen per exercise is already its most
          // recent session — no running max comparison needed.
          lastSessionDate: log.created_at,
        };
        exerciseMap.set(log.exercise_name, entry);
      }

      if (estimated1RM && estimated1RM > entry.estimated1RM) {
        entry.estimated1RM = estimated1RM;
        entry.weight = log.weight;
        entry.reps = log.reps;
        entry.date = log.created_at;
      }
      if (!isNaN(weight)) {
        entry.minWeight = entry.minWeight == null ? weight : Math.min(entry.minWeight, weight);
        entry.maxWeight = entry.maxWeight == null ? weight : Math.max(entry.maxWeight, weight);
      }
    });

    return Array.from(exerciseMap.values())
      .filter(e => e.estimated1RM > 0)
      .sort((a, b) => b.estimated1RM - a.estimated1RM);
  } catch (error) {
    console.error('Error getting all 1RMs:', error);
    return [];
  }
};

/**
 * Calculate strength increase percentage
 * Compares oldest 1RM to newest 1RM for an exercise
 *
 * Baseline/current each use the BEST of the first/last 3 logged days, not a
 * single day — same anti-outlier safeguard StrengthDashboard.jsx already
 * used ("prevents a single light 'technique day' from creating an
 * artificially low baseline that inflates % change, e.g. +442% on lateral
 * raises"). A bare first-day-vs-last-day comparison is too easily skewed by
 * one light warm-up set landing as the very first (or very last) logged day
 * in the window.
 *
 * @param {string} userId - User's auth ID
 * @param {string} exerciseName - Exercise name
 * @param {number} months - Period to compare (default 3 months)
 * @returns {Object|null} - { oldestRM, newestRM, increaseKg, increasePercent } or null
 */
export const getStrengthIncrease = async (userId, exerciseName, months = 3) => {
  try {
    const progression = await get1RMProgression(userId, exerciseName, months);

    if (progression.length < 2) return null;

    // Split at the midpoint so baseline/recent windows never overlap — taking a flat
    // "first 3 vs last 3" broke for exactly 2-3 days of data (the two slices were
    // identical, so oldestRM===newestRM always, silently reporting 0% change
    // regardless of the real trend). This preserves the original "up to 3 vs up to 3"
    // behavior once there are 6+ days (midpoint lands exactly at 3), and degrades to
    // non-overlapping halves below that instead of double-counting shared days.
    const midpoint = Math.floor(progression.length / 2);
    const baselineDays = progression.slice(0, Math.min(3, midpoint));
    const recentDays = progression.slice(-Math.min(3, progression.length - midpoint));
    const oldestRM = Math.max(...baselineDays.map(d => d.estimated1RM));
    const newestRM = Math.max(...recentDays.map(d => d.estimated1RM));
    const oldest = baselineDays.find(d => d.estimated1RM === oldestRM);
    const newest = recentDays.find(d => d.estimated1RM === newestRM);

    const increaseKg = newestRM - oldestRM;
    const increasePercent = ((increaseKg / oldestRM) * 100).toFixed(1);

    return {
      oldestRM,
      newestRM,
      increaseKg: Math.round(increaseKg * 10) / 10,
      increasePercent: parseFloat(increasePercent),
      oldestDate: oldest.date,
      newestDate: newest.date,
    };
  } catch (error) {
    console.error('Error calculating strength increase:', error);
    return null;
  }
};

/**
 * Get a three-tier Strength Progress summary for Stats > Progress.
 * Buckets a bounded pool of the user's exercises (top 20 by current 1RM, to
 * limit getStrengthIncrease's per-exercise query fan-out) into:
 *   - topImprovements: increasePercent > 3, top 3, sorted desc
 *   - needsAttention: increasePercent < -3, sorted most-negative first
 *   - newBaselines: exercises with fewer than 2 data points in the window
 *     (getStrengthIncrease returns null for these) — not "failed progression"
 * The ±3% cutoff is a display-bucketing convention layered on
 * getStrengthIncrease's unmodified output, not a new calculation.
 *
 * @param {string} userId - User's auth ID
 * @param {number} months - Comparison window (default 3 months)
 * @returns {Object} - { topImprovements, needsAttention, newBaselines, allExercises }
 */
export const getStrengthProgressSummary = async (userId, months = 3) => {
  const allExercises = await getAllEstimated1RMs(userId);
  const pool = allExercises.slice(0, 20);

  const results = await Promise.all(
    pool.map(async (ex) => {
      const increase = await getStrengthIncrease(userId, ex.exerciseName, months);
      return {
        exerciseName: ex.exerciseName, estimated1RM: ex.estimated1RM, movementCategory: ex.movementCategory,
        minWeight: ex.minWeight, maxWeight: ex.maxWeight, lastSessionDate: ex.lastSessionDate,
        increase,
      };
    })
  );

  const newBaselines = [];
  const topImprovements = [];
  const needsAttention = [];

  results.forEach(({ exerciseName, estimated1RM, movementCategory, minWeight, maxWeight, lastSessionDate, increase }) => {
    if (!increase) {
      newBaselines.push({ exerciseName, estimated1RM, movementCategory, minWeight, maxWeight, lastSessionDate });
      return;
    }
    const entry = { exerciseName, estimated1RM, movementCategory, minWeight, maxWeight, lastSessionDate, ...increase };
    if (increase.increasePercent > 3) {
      topImprovements.push(entry);
    } else if (increase.increasePercent < -3) {
      needsAttention.push(entry);
    }
  });

  topImprovements.sort((a, b) => b.increasePercent - a.increasePercent);
  needsAttention.sort((a, b) => a.increasePercent - b.increasePercent);

  return {
    topImprovements: topImprovements.slice(0, 3),
    needsAttention,
    newBaselines,
    allExercises,
  };
};

/**
 * Get strength level classification based on 1RM
 * Uses bodyweight ratio for compound lifts
 *
 * @param {string} exerciseName - Exercise name
 * @param {number} oneRM - Estimated 1RM (kg)
 * @param {number} bodyweight - User's bodyweight (kg)
 * @returns {string} - 'Beginner' | 'Novice' | 'Intermediate' | 'Advanced' | 'Elite'
 */
export const getStrengthLevel = (exerciseName, oneRM, bodyweight) => {
  if (!oneRM || !bodyweight) return null;

  const ratio = oneRM / bodyweight;

  // Strength standards (bodyweight ratio) - simplified version
  // Based on Symmetric Strength / ExRx standards for adult males
  // Note: These are approximations and vary by gender/age
  const standards = {
    'Squat': { beginner: 0.75, novice: 1.0, intermediate: 1.5, advanced: 2.0, elite: 2.5 },
    'Deadlift': { beginner: 0.75, novice: 1.0, intermediate: 1.75, advanced: 2.25, elite: 2.75 },
    'Bench Press': { beginner: 0.5, novice: 0.75, intermediate: 1.0, advanced: 1.5, elite: 2.0 },
    'Overhead Press': { beginner: 0.35, novice: 0.5, intermediate: 0.75, advanced: 1.0, elite: 1.35 },
  };

  // Find matching exercise (case insensitive, partial match)
  const exerciseKey = Object.keys(standards).find(key =>
    exerciseName.toLowerCase().includes(key.toLowerCase())
  );

  if (!exerciseKey) return null;

  const levels = standards[exerciseKey];

  if (ratio >= levels.elite) return 'Elite';
  if (ratio >= levels.advanced) return 'Advanced';
  if (ratio >= levels.intermediate) return 'Intermediate';
  if (ratio >= levels.novice) return 'Novice';
  return 'Beginner';
};
